import { Card, Grid } from '@mui/material';
import {
  Alert,
  AlertButton,
  isEmail,
  PhoneNumberField,
  Show,
} from '@side/pantry';
import { Controller } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { isValueEqualToCountryPrefix } from '@/common/utils/phoneNumber';
import { FormTextField } from '@/components/Inputs/FormTextField';
import {
  LICENSE_NUMBER_ERROR_MESSAGE,
  LICENSE_NUMBER_PATTERN,
} from '@/constants';
import { isValidPhone } from '@/views/Coversheet/utils/fieldValidation';
import { BrokerageDetails } from '@/views/Referrals/views/NewReferral/BrokerageDetails';
import type { AgentFields } from '@/views/Referrals/views/NewReferral/types';
import {
  TrackContactModalEvent,
  useTrackContactModalAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactModal';
import {
  ContactModalState,
  type IsFieldInvalidCallback,
} from './ContactFormModal.helpers';
import { ReadOnlyAgentDetails } from './ReadOnlyAgentDetails';

interface Props {
  errorMessage: string;
  eventContainer: string;
  existingContact?: AgentFields;
  onUseExistingContact: () => void;
  state: ContactModalState;
  isFieldInvalid: IsFieldInvalidCallback;
}

export function AgentContactForm({
  errorMessage,
  eventContainer,
  existingContact,
  onUseExistingContact,
  state,
  isFieldInvalid,
}: Props) {
  const trackEvent = useTrackContactModalAnalytics();

  useEffectOnce(() => {
    trackEvent(TrackContactModalEvent.Mount, { eventContainer });
  });

  if (state === ContactModalState.EXISTING_CONTACT_FOUND) {
    return (
      <Card
        sx={{
          backgroundColor: 'background.default',
          p: 2,
          mb: { xs: 2, md: 3 },
        }}
      >
        <ReadOnlyAgentDetails agent={existingContact!} />
      </Card>
    );
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    trackEvent(TrackContactModalEvent.FieldUpdate, {
      eventTarget: e.target.name,
      value: e.target.value,
    });
  };

  return (
    <Grid columnSpacing={2.5} container>
      <Show when={errorMessage}>
        <Grid size={12}>
          <Alert
            severity="error"
            action={
              <Show when={existingContact}>
                <AlertButton
                  severity="error"
                  onClick={() => {
                    onUseExistingContact();
                    trackEvent(
                      TrackContactModalEvent.ClickExistingContactBannerButton,
                      { contactId: existingContact?.userId },
                    );
                  }}
                >
                  Use Existing Contact
                </AlertButton>
              </Show>
            }
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Grid>
      </Show>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="firstName"
          label="First Name"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="lastName"
          label="Last Name"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="email"
          label="Email"
          rules={{
            validate: (value: string) => isEmail(value) || 'Invalid email',
          }}
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Controller<AgentFields, 'cellPhone'>
          name="cellPhone"
          rules={{
            validate: (value) => {
              if (isValueEqualToCountryPrefix(value)) {
                return 'Required';
              }
              return isValidPhone(value) || 'Invalid phone number';
            },
            required: true,
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <PhoneNumberField
              fixedHeight
              fullWidth
              id="cellPhone"
              label="Cell Phone"
              name="cellPhone"
              onChange={(value) => {
                field.onChange(value);
              }}
              required
              highlight={!field.value ? 'secondary' : undefined}
              error={invalid}
              helperText={error?.message}
              value={field.value}
              onBlur={(newValue) => {
                trackEvent(TrackContactModalEvent.FieldUpdate, {
                  eventTarget: 'cellPhone',
                  value: newValue,
                });

                field.onBlur();
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="licenseNumber"
          label="Agent License Number"
          required
          rules={{
            pattern: {
              value: LICENSE_NUMBER_PATTERN,
              message: LICENSE_NUMBER_ERROR_MESSAGE,
            },
          }}
          error={isFieldInvalid('licenseNumber')}
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="brokerage.companyName"
          label="Brokerage Name"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <BrokerageDetails isFieldInvalid={isFieldInvalid} />
    </Grid>
  );
}
